import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import {
  cognitoConfig,
  getCognitoLoginUrl,
  getCognitoTokenUrl,
  getCognitoUserInfoUrl,
  generatePKCEChallenge,
} from '../auth/cognito.config';
import { AuthService } from '../services/auth.service';

interface CognitoTokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
}

interface CognitoUserInfo {
  username?: string;
  ['cognito:username']?: string;
  email?: string;
}

const PENDING_SHIPMENT_ID_KEY = 'pending_shipment_id';
let callbackInProgress: Promise<boolean> | null = null;
let callbackInProgressCode: string | null = null;

function getUsernameFromIdToken(idToken?: string): string | null {
  if (!idToken) {
    return null;
  }

  const parts = idToken.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload['cognito:username'] || payload['username'] || payload['email'] || null;
  } catch {
    return null;
  }
}

async function exchangeCodeForToken(code: string): Promise<CognitoTokenResponse | null> {
  const codeVerifier = sessionStorage.getItem('pkce_verifier');
  
  if (!codeVerifier) {
    console.error('PKCE verifier not found in sessionStorage');
    return null;
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cognitoConfig.clientId,
    code,
    redirect_uri: cognitoConfig.redirectUri,
    code_verifier: codeVerifier,
  });

  try {
    const response = await fetch(getCognitoTokenUrl(cognitoConfig), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      console.error('Token exchange failed:', response.status, response.statusText);
      return null;
    }

    return (await response.json()) as CognitoTokenResponse;
  } catch (err) {
    console.error('Token exchange error:', err);
    return null;
  }
}

async function getUserName(accessToken: string, idToken?: string): Promise<string | null> {
  try {
    const response = await fetch(getCognitoUserInfoUrl(cognitoConfig), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (response.ok) {
      const userInfo = (await response.json()) as CognitoUserInfo;
      return userInfo['cognito:username'] || userInfo.username || userInfo.email || null;
    }
  } catch {
    // ignore and fallback to id_token
  }

  return getUsernameFromIdToken(idToken);
}

async function processCognitoCallbackIfNeeded(authService: AuthService): Promise<boolean> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) {
    return false;
  }

  if (callbackInProgress && callbackInProgressCode === code) {
    return callbackInProgress;
  }

  callbackInProgressCode = code;
  callbackInProgress = (async () => {
    const tokenResponse = await exchangeCodeForToken(code);

    sessionStorage.removeItem('pkce_verifier');

    if (!tokenResponse?.access_token) {
      console.error('Failed to exchange code for token');
      return false;
    }

    authService.saveTokens({
      accessToken: tokenResponse.access_token,
      idToken: tokenResponse.id_token,
      refreshToken: tokenResponse.refresh_token,
    });

    const userName = await getUserName(tokenResponse.access_token, tokenResponse.id_token);
    if (userName) {
      localStorage.setItem('user_name', userName);
    }

    // Remove OAuth params from the URL after successful callback processing.
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

    return true;
  })();

  try {
    return await callbackInProgress;
  } finally {
    callbackInProgress = null;
    callbackInProgressCode = null;
  }
}

export const authGuard: CanActivateFn = async (_route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  const currentUrl = new URL(window.location.href);
  const shipmentId = currentUrl.searchParams.get('shipmentId');
  if (shipmentId) {
    sessionStorage.setItem(PENDING_SHIPMENT_ID_KEY, shipmentId);
  }

  const callbackProcessed = await processCognitoCallbackIfNeeded(authService);
  if (callbackProcessed) {
    const pendingShipmentId = sessionStorage.getItem(PENDING_SHIPMENT_ID_KEY);
    if (pendingShipmentId) {
      return router.createUrlTree(['/driver'], { queryParams: { shipmentId: pendingShipmentId } });
    }

    return router.createUrlTree(['/home']);
  }

  const accessToken = await authService.getValidAccessToken();

  if (accessToken && accessToken.trim().length > 0) {
    return true;
  }

  const { codeVerifier, codeChallenge } = await generatePKCEChallenge();
  sessionStorage.setItem('pkce_verifier', codeVerifier);

  window.location.href = getCognitoLoginUrl(cognitoConfig, codeChallenge);
  return false;
};